import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { Observable, catchError, lastValueFrom, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { RewardsBoostCampaignResponseType } from "../api_services/RewardsBoostCampaignApiService/response/RewardsBoostCampaignResponseSchema";
import RewardsBoostCampaignApiService from "../api_services/RewardsBoostCampaignApiService/RewardsBoostCampaignApiService";
import { RewardsBoostCampaignEntity } from "../entities/entities";
import RewardsBoostCampaignQuery from "../queries/RewardsBoostCampaignQuery";
import RewardsBoostCampaignStore from "../stores/RewardsBoostCampaignStore";
import { rewardsApplicationLoggerToken } from "../tokens";
import { getLoadingStatus } from "../utils/statuses";

@singleton()
class RewardsBoostCampaignService {
    public readonly boostCampaignStatus$: Observable<STATUS>;
    public readonly boostCampaign$: Observable<RewardsBoostCampaignEntity>;

    constructor(
        private readonly rewardsBoostCampaignApiService: RewardsBoostCampaignApiService,
        private readonly rewardsBoostCampaignStore: RewardsBoostCampaignStore,
        private readonly rewardsBoostCampaignQuery: RewardsBoostCampaignQuery,
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {
        this.boostCampaignStatus$ = this.rewardsBoostCampaignQuery.status$;
        this.boostCampaign$ = this.rewardsBoostCampaignQuery.boostCampaign$;
    }

    //Api calls
    getActiveBoostCampaign(): Observable<RewardsBoostCampaignResponseType> {
        this.logger.debug(
            "RewardsBoostCampaignService.getActiveBoostCampaign is started"
        );

        const status = this.rewardsBoostCampaignStore.getStatus();
        this.rewardsBoostCampaignStore.setStatus(getLoadingStatus(status));

        return this.rewardsBoostCampaignApiService
            .getActiveBoostCampaign()
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "RewardsBoostCampaignService.getActiveBoostCampaign is errored",
                        error
                    );
                    this.rewardsBoostCampaignStore.setStatus(STATUS.ERROR);
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "RewardsBoostCampaignService.getActiveBoostCampaign is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.rewardsBoostCampaignStore.update((state) => ({
                            ...state,
                            boostCampaign: value.boostCampaign,
                        }));
                        this.rewardsBoostCampaignStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetActiveBoostCampaign(): Promise<void> {
        if (this.rewardsBoostCampaignStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getActiveBoostCampaign());
    }
}

export default RewardsBoostCampaignService;
